import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  getSectionOgImageUrl,
} from "@config/ogImageConstants";
import {
  getAuthorsName,
  getChapter1,
  getContentCat,
  getLevel2,
  getTagsName,
} from "@elements/GAData/GADataHelper";
import { formatDate } from "@helper/useDateFormat";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import { GADataProps } from "@src/app/types/GA";
import { MetaTagsProps } from "@src/app/types/MetaTags";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { RawImageField } from "@typings/Opensearch";
import type { PageMetaDataValues } from "@typings/PageMetaData";
import {
  AdsCardProps,
  FinishCardProps,
  QuizCardProps,
} from "@typings/QuickNews";

import { getFirstParagraph } from "../Article/ArticleMetaData";

type CardsMetaDataProps = {
  card?:
    | ProcessedArticleData
    | QuizCardProps
    | AdsCardProps
    | FinishCardProps
    | LatestVideoResponse;
  activeTab: string;
  onBoarded: boolean;
  pagination: number;
};

export default function QuickNewsMetaData({
  card,
  activeTab,
  onBoarded,
  pagination,
}: CardsMetaDataProps): PageMetaDataValues {
  const isOnBoarded = onBoarded && !!card;
  const isArticle =
    isOnBoarded &&
    !("adType" in card || "quizzes" in card || "endCard" in card) &&
    "id" in card &&
    "title" in card;

  const cardTitle = isOnBoarded
    ? "title" in card
      ? card.title
      : "name" in card
        ? card.name
        : ""
    : "";
  const cardId = isOnBoarded && "id" in card ? card.id : "";
  const defaultMetaTitle = "Quick News & Shorts by Berita Harian";
  const defaultMetaDesc =
    "Catch the latest shorts & easy-to-read news. Quick News has politics, society, popular headlines, videos; short, fast.";
  const defaultMetaSlug = "quick-news/";

  const level2 = isArticle ? getLevel2(card) : "";
  const keywords = isArticle ? getTagsName(card.tags || []) : [];

  /* -----------------------------
   * GA DATA
   * ----------------------------- */
  const gaData = isOnBoarded
    ? ({
        title: cardTitle,
        level2: "quick-news",
        chapter1: isArticle ? getChapter1(card) : "",
        contentcat: isArticle ? getContentCat(card.contentAccess) : "1",
        section: level2,
        contenttype: "1",
        pagination: pagination + 1,
        author: "authors" in card ? getAuthorsName(card.authors || []) : "",
        cue_articleid: isArticle ? cardId : "",
        articleid: isArticle ? cardId : "",
        keyword: keywords,
        pubdate:
          "datetime" in card
            ? formatDate(card.datetime, "yyyy-MM-dd HH:mm:ss")
            : "",
        content_publication_date_utc: "datetime" in card ? card.datetime : "",
        content_update_date_utc: "dateEdited" in card ? card.dateEdited : "",
      } as GADataProps)
    : ({
        level2: "quick-news",
        title: "quick_news_index",
        section: "quick-news",
        chapter1: "",
        contentcat: "1",
        contenttype: undefined,
        pagination: 0,
      } as GADataProps);

  /* -----------------------------
   * META TITLE
   * ----------------------------- */
  const metaTitle = isOnBoarded ? cardTitle : defaultMetaTitle;

  /* -----------------------------
   * META DESCRIPTION
   * ----------------------------- */
  const articleMetaDescription = isArticle
    ? level2 === "kaki makan" && card.location?.title && card.location.address
      ? `Baca ulasan lengkap tentang ${card.location.title}, hidangan popular & suasana makan di ${card.location.address}. Panduan kaki makan Singapura oleh Berita Harian.`
      : "seoFields" in card &&
          card.seoFields?.metaDescription &&
          card.seoFields.metaDescription.trim() !== ""
        ? card.seoFields.metaDescription
        : `Berita - ${"articleBody" in card && getFirstParagraph(card.articleBody, 110)}. Baca lebih lanjut di BeritaHarian.sg`
    : "";
  const metaDesc = isOnBoarded ? articleMetaDescription || "" : defaultMetaDesc;

  /* -----------------------------
   * META SLUG
   * ----------------------------- */
  const metaSlug = isOnBoarded
    ? `quick-news/${activeTab === "Videos" ? `video/${cardId}` : cardId}`
    : defaultMetaSlug;

  /* -----------------------------
   * META Image
   * ----------------------------- */
  // Every path needs a resolvable URL: an unguarded template literal would
  // emit the truthy but broken "undefined?w=1200", and an empty string would
  // make getMetaData() fall back to the legacy BH logo.
  const fallbackOgImageUrl = getSectionOgImageUrl("default");
  const ArticleOgImageUrl = isArticle
    ? card.ogImageUrl || card.imageField?.url
    : undefined;
  const metaImage = isArticle
    ? activeTab === "MyFeed" && "imageField" in card
      ? ArticleOgImageUrl
        ? `${ArticleOgImageUrl}?w=${DEFAULT_OG_IMAGE_WIDTH}`
        : fallbackOgImageUrl
      : activeTab === "Videos" && "thumbnail" in card && card.thumbnail
        ? card.thumbnail
        : fallbackOgImageUrl
    : fallbackOgImageUrl;

  const ogImage = {
    url: metaImage,
    width: DEFAULT_OG_IMAGE_WIDTH,
    height: DEFAULT_OG_IMAGE_HEIGHT,
  } as RawImageField;

  const metaTags: MetaTagsProps = {
    title: metaTitle,
    description: metaDesc,
    slug: metaSlug,
    image: ogImage,
    ogImage: ogImage,
  };

  return {
    gaData,
    metaTags,
  };
}
